import { UserContentType } from "@/types";

// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = (obj: Record<string, any>): boolean =>
  Object.keys(obj).length === 0;

// ** Returns K format from a number
export const kFormatter = (num: number): string | number => {
  return num > 999 ? `${(num / 1000).toFixed(1)}k` : num;
};

// ** Converts HTML to string
export const htmlToString = (html: string): string =>
  html.replace(/<\/?[^>]+(>|$)/g, '');

// ** Checks if the passed date is today
const isToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

export const formatDateToString = (date: Date): string => {
  return date.toLocaleString("tr-TR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};
export const uuidv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}
/**
 ** Format and return date in Humanize format
 * @param value date to format
 * @param formatting Intl object to format with
 */
export const formatDate = (
  value: string,
  formatting: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }
): string => {
  if (!value) return value;
  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value));
};

// ** Returns short month of passed date
export const formatDateToMonthShort = (
  value: string,
  toTimeForCurrentDay: boolean = true
): string => {
  const date = new Date(value);
  let formatting: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: 'numeric', minute: 'numeric' };
  }

  return new Intl.DateTimeFormat('en-US', formatting).format(date);
};

// ** Return if user is logged in
export const isUserLoggedIn = (): string | null =>
  localStorage.getItem('userData');

export const getUserData = (): Record<string, any> | null =>
  JSON.parse(localStorage.getItem('userData') || 'null');

/**
 ** This function is used for demo purpose route navigation
 * @param userRole Role of user
 */
export const getHomeRouteForLoggedInUser = (userRole: string): string => {
  if (userRole === 'admin') return '/';
  if (userRole === 'client') return '/access-control';
  return '/login';
};

// ** React Select Theme Colors
export const selectThemeColors = (theme: Record<string, any>): Record<string, any> => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: '#7367f01a',
    primary: '#7367f0',
    neutral10: '#7367f0',
    neutral20: '#ededed',
    neutral30: '#ededed',
  },
});

// ** Merge arrays by a property

// ** Convert image URL to Base64
export const imageUrlToBase64 = async (url: string): Promise<string> => {
  const response = await fetch(url, { cache: 'no-cache' });
  const blob = await response.blob();
  return new Promise((onSuccess, onError) => {
    try {
      const reader = new FileReader();
      reader.onload = function () {
        onSuccess(this.result as string);
      };
      reader.readAsDataURL(blob);
    } catch (e) {
      onError(e);
    }
  });
};

// ** Convert image URL to ByteArray
export const imageUrlToByteArray = async (url: string): Promise<Uint8Array> => {
  const response = await fetch(url, { cache: 'no-cache' });
  const blob = await response.blob();
  const arrayBuffer = await blob.arrayBuffer();
  return new Uint8Array(arrayBuffer);
};

// ** Convert ByteArray to Base64
export const byteArrayToBase64 = (byteArray: string): string => {
  return `data:image/png;base64,${byteArray}`;
};

// ** Check if pageItem is an activity
export const isActivity = (pageItem: { pageItemType: number }): boolean => {
  return pageItem.pageItemType > 80;
};

// ** Validate URL
export const isValidURL = (string: string): boolean => {
  try {
    const url = new URL(string); // Eğer URL geçerli değilse hata fırlatır
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (_) {
    return false;
  }
};

// ** Update order based on index
export const updateOrderBasedOnIndex = <T extends { id: number; order: number }>(
  array: T[]
): T[] => {
  if (!array || array.length === 0) return array;

  return array.map((item, index) => {
    const isDuplicate = array.some(
      (otherItem) => otherItem.id !== item.id && otherItem.order === item.order
    );

    return isDuplicate ? { ...item, order: index } : item;
  });
};

export const mergeByProperty = <T extends {}>(
  target: T[],
  source: T[],
  prop: keyof T
): void => {
  target = target || [];

  source.forEach((sourceElement) => {
    const targetElement = target.find(
      (targetElement) => sourceElement[prop] === targetElement[prop]
    );
    targetElement
      ? Object.assign(targetElement, sourceElement)
      : target.push(sourceElement);
  });
};

export function getExtensionsForContentType(contentType: UserContentType): string[] {
  switch (contentType) {
    case UserContentType.Image:
      return ['jpg', 'png', 'gif', 'bmp', 'svg'];
    case UserContentType.Video:
      return ['mp4', 'mkv', 'avi', 'mov', 'flv'];
    case UserContentType.Sound:
      return ['mp3', 'wav', 'ogg', 'aac', 'flac'];
    case UserContentType.File:
      return ['pdf', 'doc', 'docx', 'xls', 'xlsx'];
    default:
      return []; // Diğer türler için boş liste döndür
  }
}

export const openWebSide = (url: string) => {
  const width = window.screen.width;
  const height = window.screen.height;
  const newWindow = window.open(
    url,
    '_blank',
    `width=${width - 200},height=${height - 200},top=0,left=0`,
  );
  if (newWindow != null) {
    if (newWindow.window.screen && newWindow.window.screen.availWidth) {
      newWindow.moveTo(0, 0);
      newWindow.resizeTo(
        newWindow.screen.availWidth,
        newWindow.screen.availHeight,
      );
    }
  }
};

